import { useState } from 'react';

import { useOrganizationId } from '@/contexts/OrganizationContext';
import { useSnackbar } from '@/hooks/useSnackbar';
import { useSwapDistributorRoleForDistributorGroup } from '@/hooks/useSwapDistributorRoleForDistributorGroup';
import { GroupType } from '@/shared/types/groupType';
import { handleSnackbarError } from '@/shared/utils/errorHandler';

export interface SwapModalData {
  userId: string;
  userEmail: string;
  currentGroupId: number;
  currentGroupName: string;
  currentGroupType?: GroupType;
  newGroupId: number;
  newGroupName: string;
}

interface UseDistributorSwapModalReturn {
  swapModalOpen: boolean;
  swapModalData: SwapModalData | null;
  openSwapModal: (data: SwapModalData) => void;
  closeSwapModal: () => void;
  confirmSwap: () => Promise<void>;
  isSwappingRole: boolean;
}

export function useDistributorSwapModal(): UseDistributorSwapModalReturn {
  const { organizationId } = useOrganizationId();
  const snackbar = useSnackbar();
  const { swapDistributorRole, isSwappingRole } = useSwapDistributorRoleForDistributorGroup();

  const [swapModalOpen, setSwapModalOpen] = useState(false);
  const [swapModalData, setSwapModalData] = useState<SwapModalData | null>(null);

  const openSwapModal = (data: SwapModalData) => {
    setSwapModalData(data);
    setSwapModalOpen(true);
  };

  const closeSwapModal = () => {
    setSwapModalOpen(false);
    setSwapModalData(null);
  };

  const confirmSwap = async () => {
    if (!swapModalData) {
      return;
    }

    try {
      const result = await swapDistributorRole({
        organizationId,
        params: {
          userId: swapModalData.userId,
          currentDistributorGroupId: swapModalData.currentGroupId,
          newDistributorGroupId: swapModalData.newGroupId,
        },
      });

      if (result) {
        snackbar.showSuccess('Distributor role changed successfully');
      }
    } catch (err) {
      handleSnackbarError(err, snackbar, 'Failed to update user role');
    } finally {
      closeSwapModal();
    }
  };

  return {
    swapModalOpen,
    swapModalData,
    openSwapModal,
    closeSwapModal,
    confirmSwap,
    isSwappingRole,
  };
}
